import { PlanTrailActionContext, getMap } from "./types";
import { POIUtility } from "../../../../../utils/models/POI";
import { MapLayerCategory } from "../../../../../utils/layer-registry";
import mapboxgl from "mapbox-gl";

export const highlightActions = {
  async addHighlightsToMap({ state, dispatch }: PlanTrailActionContext) {
    const map = getMap();
    if (!map) return;

    const layerRegistry = window.layerRegistry;

    // Remove existing highlight layers first
    await dispatch('removeHighlightsFromMap');


    // Add each highlight as a marker on the map
    state.hikeHighlights.forEach((highlight, index) => {
      const id = `highlight-${highlight.clientId}`;

      // Get the icon name from POIUtility
      let iconName = 'highlight_icon_custom'; // default
      try {
        const poiUtil = POIUtility.fromString(highlight.type);
        iconName = poiUtil.hikeHighlightIcon();
      } catch (e) {
        console.warn(`Unknown POI type: ${highlight.type}, using default icon`);
      }

      // Create GeoJSON source for the highlight
      const source = {
        type: 'geojson',
        data: {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: highlight.coordinates
          },
          properties: {
            clientId: highlight.clientId,
            type: highlight.type,
            title: highlight.title,
            description: highlight.description,
            dayNumber: highlight.dayNumber
          }
        }
      };

      // Create symbol layer for the highlight
      const layer: mapboxgl.SymbolLayer = {
        id: id,
        type: 'symbol',
        source: id,
        layout: {
          'icon-image': iconName,
          'icon-size': 0.3,
          'icon-allow-overlap': true,
          'text-field': highlight.title,
          'text-font': ['Roboto Regular', 'Arial Unicode MS Regular'],
          'text-size': 12,
          'text-offset': [0, 1.5],
          'text-anchor': 'top',
          'text-optional': true
        },
        paint: {
          'text-color': '#333',
          'text-halo-color': '#fff',
          'text-halo-width': 2
        }
      };

      // Add layer and source to map
      // Above the route's markers and the map's own place icons (the Top
      // group's poi-layer) — a highlight on a place is what should show, and
      // take the click — but under the day stops' rings (Top, z 20).
      layerRegistry.addLayer(layer, MapLayerCategory.Top, {
        z: 15,
        source: { id, spec: source, replace: true }
      });

      // Add click listener for the highlight
      map.on('click', id, (e) => {
        if (e.features && e.features.length > 0) {
          const feature = e.features[0];
          const props = feature.properties;
          
          // Show highlight detail dialog
          if (window.showHighlightDetail) {
            window.showHighlightDetail({
              clientId: props.clientId,
              type: props.type,
              title: props.title,
              description: props.description,
              dayNumber: props.dayNumber,
              coordinates: feature.geometry.coordinates
            });
          }
          
          // Prevent event from bubbling
          e.originalEvent.stopPropagation();
        }
      });

      // Change cursor on hover
      map.on('mouseenter', id, () => {
        map.getCanvas().style.cursor = 'pointer';
      });

      map.on('mouseleave', id, () => {
        map.getCanvas().style.cursor = '';
      });
    });
  },

  async removeHighlightsFromMap({ state }: PlanTrailActionContext) {
    const map = getMap();
    if (!map) return;

    // Remove all highlight layers
    state.hikeHighlights.forEach(highlight => {
      const id = `highlight-${highlight.clientId}`;

      // Remove event listeners
      map.off('click', id);
      map.off('mouseenter', id);
      map.off('mouseleave', id);

      // Remove layer and source
      if (map.getLayer(id)) {
        map.removeLayer(id);
      }
      if (map.getSource(id)) {
        map.removeSource(id);
      }
    });
  },
  
  async removeHighlightFromMap({ }: PlanTrailActionContext, clientId: string) {
    const map = getMap();
    if (!map) return;
    
    const id = `highlight-${clientId}`;
    
    // Remove event listeners
    map.off('click', id);
    map.off('mouseenter', id);
    map.off('mouseleave', id);
    
    // Remove layer and source
    if (map.getLayer(id)) {
      map.removeLayer(id);
    }
    if (map.getSource(id)) {
      map.removeSource(id);
    }
  },

  /**
   * Deletes a highlight — from its sheet or the day's list. A stop pinned to
   * it (a Google Maps place, say) has nothing to be pinned to any more, so
   * the stop comes unpinned too; its marker stays where it is.
   */
  async deleteHikeHighlight({ state, commit, dispatch }: PlanTrailActionContext, clientId: string) {
    await dispatch('removeHighlightFromMap', clientId);
    commit('removeHikeHighlight', clientId);

    const pinnedTo = (destination: any) => destination?.itemType === 'highlight' && destination.itemId === clientId;
    const unpinnedDays = Object.keys(state.stageDestinations ?? {}).filter(day => pinnedTo(state.stageDestinations[Number(day)]));
    unpinnedDays.forEach(day => commit('removeStageDestination', Number(day)));
    const unpinnedStart = pinnedTo(state.trailStartDestination);
    if (unpinnedStart) commit('removeTrailStartDestination');
    if (unpinnedDays.length || unpinnedStart) {
      await dispatch('addConsolidatedMarkersToMap');
      await dispatch('updateStageConnectorsOnMap');
      await dispatch('updateTrailEndpointMarkersOnMap');
    }
    await dispatch('refreshHighlights');
  },

  async refreshHighlights({ dispatch }: PlanTrailActionContext) {
    // Remove and re-add all highlights (useful after changes)
    await dispatch('removeHighlightsFromMap');
    await dispatch('addHighlightsToMap');
  }
};
